import React, { useState, useMemo, useEffect, useCallback } from "react";
import { toast } from "react-hot-toast";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import SoftButton from "../SoftButton";
import KpiModal from "../../pages/ManualEntry/KpiModal";
import { useNavigate } from "react-router-dom";

// uuid is a library for generating unique id
import { v4 as uuidv4 } from "uuid";

// @mui material components
import { Popover, Dialog, Tooltip } from "@mui/material";
import { Spinner } from "../shared/Loading";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSelector } from "react-redux";

// Icons
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ReactComponent as TimelineIcon } from "../../img/sk-timelineicon.svg";
import { ReactComponent as AttachLinkIcon } from "../../img/sk-linkicon.svg";
import { ReactComponent as MetricScoreIcon } from "../../img/sk-settingicon.svg";
import IntegratedMetricInfo from "./IntegratedMetricInfo";

// Soft UI Dashboard PRO React components
import SoftBox from "../SoftBox";
import SoftInput from "../SoftInput";
import SoftTypography from "../SoftTypography";
import SoftProfile from "../ProfilePictures/SoftProfile";

// Soft UI Dashboard PRO React base styles
import colors from "../../assets/theme/base/colors";
import typography from "../../assets/theme/base/typography";
import borders from "../../assets/theme/base/borders";
import AddLinkModal from "./AddLinkModal";

import ViewAllLinksModal from "./ViewAllLinksModal";
import ManageLinks from "./ManageLinks";
import TotalScoreCell from "./TotalScoreCell";
// import "@szhsin/react-menu/dist/index.css";
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";
import MetricScoreConfig from "./MetricScoreConfig";

// Hooks
import { UseSelectedWorkspace, UseCompany } from "../../hooks/company";

// Hooks
import { useTotalScoreSave, UseGroupAllKpis } from "../../hooks/kpi";

// Scorekeeper UX extras
import RankPill from "./RankPill";
import { useRankHistory } from "./useRankHistory";
import CellCelebration from "./CellCelebration";

// API services
import { GET_KPI_PREFERENCES, SET_KPI_PREFERENCES } from "../../api/preference";

// Helper scripts
import { formatNumber } from "../../helpers/formatter";
import { formatKpiPercent } from "../../helpers/scoreAggregate";
import { isCompositeKpi, getCompositeFormulaLabel, resolveMonthKpiKey, isKpiScorekeeperOnly } from "../../constants/kpi";

// Constants
const PREFERENCES_CATEGORY = {
  KPI_COLUMN_ORDERING: "kpiColumnOrdering",
};

const SCORE_ENTRY_MODE = {
  ALL: "All",
  TOTAL_SCORE: "TotalScore",
  INCREMENTAL: "Incremental",
};

const MODAL_METRIC_SCORE = "METRIC_SCORE";
const MODAL_MANAGE_LINKS = "MANAGE_LINKS";
const MODAL_ADD_LINK = "ADD_LINK";
const MODAL_VIEW_LINKS = "VIEW_LINKS";
const MODAL_TECH_HOURS_UPLOAD = "TECH_HOURS_UPLOAD";

const CSV_UPLOAD_METRIC_NAMES = ["tech hours", "rev cred hr", "test", "justine"];

const isTechHoursColumn = (columnName) =>
  typeof columnName === "string" &&
  CSV_UPLOAD_METRIC_NAMES.includes(columnName.trim().toLowerCase());

function renderScorekeeperCell({
  kpi,
  groupKpis,
  compositeNameLookup,
  cellValue,
  user,
  rowIndex,
  index,
  isTotalScoreMode,
  onSave,
  onAdvanceRow,
  filterUsersDataLength,
  isSavingExternal,
  monthKey = null,
}) {
  if (isCompositeKpi(kpi)) {
    return (
      <Tooltip
        title={
          isSavingExternal
            ? "Recomputing…"
            : `Calculated: ${getCompositeFormulaLabel(kpi, compositeNameLookup || groupKpis) || "source metrics"}. Edit the source metrics instead.`
        }
        placement="top"
      >
        <SoftBox
          sx={{
            height: "3.25rem",
            border: `2px dashed ${isSavingExternal ? "#A78BFA" : "#D0D5DD"}`,
            borderRadius: "6px",
            backgroundColor: isSavingExternal ? "#F5F3FF" : "#F9FAFB",
            padding: "10px 14px",
            cursor: "help",
            display: "flex",
            alignItems: "center",
          }}
        >
          {isSavingExternal ? (
            <Spinner size={18} color="#7C3AED" />
          ) : (
            <SoftTypography
              sx={{
                fontFamily: "DM Sans",
                color: "#667085",
                fontSize: "16px",
                fontWeight: 500,
                lineHeight: 1.2,
                fontFeatureSettings: '"tnum" 1',
                letterSpacing: "-0.005em",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {cellValue?.formatted}
            </SoftTypography>
          )}
        </SoftBox>
      </Tooltip>
    );
  }

  if (isTotalScoreMode) {
    // monthKey in the React key forces a clean mount when switching July ↔ live
    // month; otherwise draft/lastSaved from the past month sticks and looks like
    // zeros / "active" cells on the current month board.
    return (
      <TotalScoreCell
        key={`${user.userId}-${kpi._id}-${monthKey ?? "live"}`}
        kpiId={kpi._id}
        userId={user.userId}
        rowIndex={rowIndex}
        colIndex={index}
        monthKey={monthKey}
        initialAmount={cellValue?.orig ?? 0}
        onSave={onSave}
        isSavingExternal={isSavingExternal}
        onAdvanceRow={(r, c, el) => onAdvanceRow(r, c, el, filterUsersDataLength)}
      />
    );
  }

  return (
    <SoftBox
      // Score cell — incremental column (with "+").
      // Desktop (≥1100px): "+" sits INLINE next to the value.
      //   Column width is wide enough that the full number always fits — no
      //   ellipsis.
      // Below 1100px: "+" jumps to the top-right corner so the value owns the
      //   full cell row in tight layouts.
      data-cell-id={`scorecell-${user.userId}-${kpi._id}`}
      sx={{
        position: "relative",
        height: "3.25rem",
        border: "2px solid #E9ECEF",
        borderRadius: "6px",
        boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.05)",
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        background: "#FFFFFF",
        "@media (max-width: 1100px)": {
          padding: "10px 28px 10px 14px",
        },
      }}
    >
      <SoftTypography
        sx={{
          fontFamily: "DM Sans",
          color: "#191D23",
          fontSize: "16px",
          fontWeight: 500,
          lineHeight: 1.2,
          fontFeatureSettings: '"tnum" 1',
          letterSpacing: "-0.005em",
          whiteSpace: "nowrap",
          "@media (max-width: 1100px)": {
            width: "100%",
          },
        }}
      >
        {cellValue?.formatted}
      </SoftTypography>
      <SoftButton
        aria-label="Add to score"
        sx={{
          borderRadius: "999px",
          minWidth: "max-content",
          minHeight: "max-content",
          width: "22px",
          height: "22px",
          padding: 0,
          color: "#98A2B3",
          flexShrink: 0,
          boxShadow: "none",
          "& svg": { fontSize: "16px !important" },
          "@media (max-width: 1100px)": {
            position: "absolute",
            top: "4px",
            right: "4px",
            width: "20px",
            height: "20px",
            background: "rgba(255,255,255,0.85)",
            boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
            "& svg": { fontSize: "14px !important" },
          },
          transition: "top 140ms ease, right 140ms ease",
        }}
      >
        <AddIcon />
      </SoftButton>
    </SoftBox>
  );
}

// Memoized row component to avoid unnecessary re-renders
function SoftTableRow({
  row,
  columns,
  gridStyle,
  borderWidth,
  light,
  TABLE_USER_COLUMN_WIDTH,
  TABLE_COLUMN_WIDTH,
  navigate,
}) {
  const rowKey = useMemo(() => `row-${row.User?.[3] || uuidv4()}`, [row.User]);
  const playerStatus = useMemo(() => `${row.User?.[4]}`, [row.User]);
  return (
    <SoftBox
      key={rowKey}
      sx={{
        display: "grid",
        gridTemplateColumns: gridStyle,
        alignItems: "center",
        boxShadow:
          "6px 6px 6px 0px rgba(177, 176, 176, 0.07), -4px -4px 6px 0px rgba(180, 177, 177, 0.07)",
        background: playerStatus === "false" ? "#EAECF0" : "transparent",
      }}
    >
      {columns.map(({ name, align }) => {
        let template;
        if (Array.isArray(row[name])) {
          template = (
            <SoftBox
              key={uuidv4()}
              p={1}
              borderBottom={
                row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null
              }
              sx={{
                cursor: "pointer",
                minWidth: TABLE_USER_COLUMN_WIDTH,
              }}
              onClick={() =>
                navigate(`/scorekeeper/timeline?user=${row[name][3]}`)
              }
            >
              <SoftBox
                display="flex"
                alignItems="center"
                py={0.5}
                px={1}
                sx={{ width: "100%" }}
              >
                <SoftBox mr={1}>
                  <SoftProfile
                    src={row[name][0]}
                    name={row[name][1]}
                    size="md"
                    borderRadius="md"
                  />
                </SoftBox>
                <SoftBox sx={{ width: "100%" }}>
                  <SoftBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      width: "100%",
                    }}
                  >
                    <SoftTypography
                      sx={{
                        fontFamily: "DM Sans",
                        color: "#191D23",
                        fontWeight: 500,
                        fontSize: "15px",
                        lineHeight: 1.3,
                        letterSpacing: "-0.005em",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row[name][1]}
                    </SoftTypography>
                    {row[name][5] /* rankDelta */ && (
                      <SoftBox sx={{ flexShrink: 0 }}>{row[name][5]}</SoftBox>
                    )}
                  </SoftBox>
                  <SoftTypography
                    sx={{
                      fontFamily: "DM Sans",
                      width: "max-content",
                      color: "#98A2B3",
                      fontSize: "12px",
                      fontWeight: 400,
                      marginTop: "2px",
                    }}
                  >
                    {row[name][2]}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </SoftBox>
          );
        } else {
          template = (
            <SoftBox
              key={uuidv4()}
              p={1}
              textAlign={align}
              borderBottom={
                row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null
              }
              color="#343A40"
              minWidth={TABLE_COLUMN_WIDTH}
            >
              {row[name]}
            </SoftBox>
          );
        }
        return template;
      })}
    </SoftBox>
  );
}
const MemoizedSoftTableRow = React.memo(SoftTableRow);

function Table({ groupKpis, usersKpis, monthRef, filterIndex, isSelected }) {
  const groupAllKpis = UseGroupAllKpis();
  const compositeNameLookup = useMemo(() => {
    const byId = new Map();
    [...(groupAllKpis || []), ...(groupKpis || [])].forEach((kpi) => {
      if (kpi?._id != null) byId.set(String(kpi._id), kpi);
    });
    return Array.from(byId.values());
  }, [groupAllKpis, groupKpis]);
  const navigate = useNavigate();
  const workspace = UseSelectedWorkspace();
  const company = UseCompany();
  // Narrower minimum column widths on mobile only — User+Points then fit
  // most phone widths without horizontal scroll. Desktop keeps 300/160px.
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  // Per-cell celebration triggers — bumped after a successful save so the
  // corresponding cell briefly pulses + shows a "+amount" floater. Keyed by
  // `${userId}-${kpiId}` so multiple cells can celebrate independently.
  const [celebrations, setCelebrations] = useState({});
  const triggerCelebration = useCallback((userId, kpiId, amount, kind, kpiMeta = null) => {
    const cellKey = `${userId}-${kpiId}`;
    // Format "+amount" appropriately to the KPI kind ($/%/number).
    let label;
    if (amount == null || amount === "") {
      label = null;
    } else if (kind === "$") {
      label = `+$${formatNumber(amount, "$")?.toString().replace("$", "")}`;
    } else if (kind === "%") {
      label = `+${formatKpiPercent(kpiMeta, parseFloat(amount))}`;
    } else {
      label = `+${amount}`;
    }
    setCelebrations((prev) => ({
      ...prev,
      [cellKey]: {
        key: (prev[cellKey]?.key || 0) + 1,
        amount: label,
      },
    }));
  }, []);

  const [metricSettingsData, setMetricSettingsData] = useState({});
  const [columns, setColumns] = useState([]);
  const [currentKpiId, setCurrentKpiId] = useState(null);
  const [pendingCompositeCells, setPendingCompositeCells] = useState(() => new Set());

  // Clear composite-pending state when the background KPI refresh completes.
  const isKpiRefreshing = useSelector((state) => state.kpiReducer.isLoading);
  useEffect(() => {
    if (!isKpiRefreshing) {
      setPendingCompositeCells((prev) => {
        return prev.size > 0 ? new Set() : prev;
      });
    }
  }, [isKpiRefreshing]);

  const [kpiIdparam, setKpiId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [currentKpiLinksData, setKpiLinksData] = useState({
    kpiId: null,
    kpiLinks: null,
  });

  const [modalFlags, setModalFlags] = useState({
    [MODAL_METRIC_SCORE]: false,
    [MODAL_MANAGE_LINKS]: false,
    [MODAL_ADD_LINK]: false,
    [MODAL_VIEW_LINKS]: false,
  });

  const { light } = colors;
  const { size, fontWeightBold } = typography;
  const { borderWidth } = borders;
  const disabledColumns = ["User", "Points"];

  // Columns actually rendered (see renderColumns below) — a stale preferences
  // race can briefly leave `columns` holding a KPI a month filter already
  // dropped from `groupKpis`, so the rendered list can be shorter than the
  // full `columns` state. react-beautiful-dnd's drag indices are indices into
  // whatever list is actually rendered, so handleOnDragEnd must reorder THIS
  // list too — reading/writing against the full `columns` array off these
  // indices reordered the wrong column (or read past the end and threw),
  // which aborted the handler before the save ever ran.
  const visibleColumns = useMemo(() => {
    const allowedIds = new Set(
      (Array.isArray(groupKpis) ? groupKpis : []).map((k) => String(k._id)),
    );
    return columns.filter(
      (col) =>
        disabledColumns.includes(col.name) ||
        (col._id != null && allowedIds.has(String(col._id))),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, groupKpis]);

  const TABLE_USER_COLUMN_WIDTH = isMobile ? "220px" : "300px";
  const TABLE_COLUMN_WIDTH = isMobile ? "120px" : "160px";

  const gridStyle = useMemo(
    () =>
      Array.from(columns).reduce((str, column) => {
        let gridFr = "1fr ";
        if (column.name === "User") {
          gridFr = "1.5fr ";
        }
        str += gridFr;
        return str;
      }, ""),
    [columns]
  );

  const initializeOrderedColumnHeaders = useCallback(
    (orderedKpis = [], kpiScoreEntryModes = []) => {
      const cols = [
        { name: "User", align: "center", width: "300px", _id: "user-field" },
        { name: "Points", align: "center", _id: "points-field" },
      ];

      const groupKpiIds = [...new Set(groupKpis?.map((x) => x._id) || [])];

      // Remove orderedKPI entries that may have been deleted from the fresh
      // groupKPIs list, and dedupe defensively — a stale saved preference
      // should never be allowed to resurrect a duplicate column.
      let mergedKPIs = [...new Set(orderedKpis)].filter((x) => groupKpiIds.includes(x));

      // Append new groupKPIs to the last columns
      mergedKPIs = [
        ...mergedKPIs,
        ...groupKpiIds.filter((x) => !mergedKPIs.includes(x)),
      ];

      // Use the stored KPI column ordering preferences for displaying the column headers
      mergedKPIs?.forEach((kpiId) => {
        const kpiData = groupKpis.find((kpi) => kpi._id === kpiId);

        const columnContainer = {
          _id: kpiData?._id,
          kpiLinks: kpiData?.kpiLinks,
          name: kpiData?.title,
          align: "center",
          icon: kpiData?.isManual === false ? <IntegratedMetricInfo kpi={kpiData} /> : null,
          // Scorekeeper-only metrics are scored here but never appear on the
          // players' boards — the one column type an admin must recognise at a
          // glance, so it gets the amber marker below.
          scorekeeperOnly: isKpiScorekeeperOnly(kpiData),
        };

        cols.push(columnContainer);
      });

      // Initialize the KPI metrics preferences
      const kpiSettings = groupKpis.reduce((list, item) => {
        const entryMode =
          kpiScoreEntryModes.find((x) => x._id === item._id)?.mode ??
          SCORE_ENTRY_MODE.INCREMENTAL;

        return [
          ...list,
          {
            kpiId: item._id,
            isAdminAccess: item.isAdminAccess,
            isIncremental: [
              SCORE_ENTRY_MODE.INCREMENTAL,
              SCORE_ENTRY_MODE.ALL,
            ].includes(entryMode),
            isTotalScore: [
              SCORE_ENTRY_MODE.TOTAL_SCORE,
              SCORE_ENTRY_MODE.ALL,
            ].includes(entryMode),
          },
        ];
      }, []);

      setColumns(cols);
      setMetricSettingsData(kpiSettings);
    },
    [groupKpis],
  );

  // Saved column ordering + score-entry modes, fetched ONCE per workspace and
  // kept in state. Columns used to rebuild in raw KPI order on every groupKpis
  // update and then re-sort when a fresh preferences response landed — with
  // scores streaming in, that effect re-ran several times and the header
  // visibly shuffled (raw order → saved order, repeatedly). Holding the saved
  // ordering in state means every rebuild already knows it: the header paints
  // in the right order the first time and never snaps back mid-load.
  const [columnPrefs, setColumnPrefs] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setColumnPrefs(null);

    GET_KPI_PREFERENCES(workspace?._id)
      .then((response) => {
        if (cancelled) return;
        setColumnPrefs({
          kpiColumns: response?.data?.kpiColumns ?? [],
          scoreEntryModes: response?.data?.scoreEntryModes ?? [],
        });
      })
      .catch(() => {
        if (cancelled) return;
        setColumnPrefs({ kpiColumns: [], scoreEntryModes: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [workspace?._id]);

  useEffect(() => {
    // Wait for the saved ordering before the first paint — a raw-order header
    // that reorders itself a second later reads as broken. The catch above
    // resolves to empty prefs, so an error still renders (in raw order).
    // Rebuilding here on every groupKpis change keeps the month-filter
    // guarantee: deleted metrics drop out because the merge filters against
    // the fresh KPI list, and there is no in-flight response to resurrect them.
    if (!columnPrefs) return;
    initializeOrderedColumnHeaders(columnPrefs.kpiColumns, columnPrefs.scoreEntryModes);
  }, [columnPrefs, initializeOrderedColumnHeaders]);

  // Manage Modal State, Visibility and Functions
  const handleOpenModal = ({ kpiId, targetModal, kpiLinks }) => {
    if (!targetModal) return;

    setModalFlags((prev) => ({ ...prev, [targetModal]: true }));
    setCurrentKpiId((prev) => kpiId);
    setKpiLinksData((prev) => ({ ...prev, kpiId, kpiLinks }));
  };

  const handleCloseModal = (targetModal) => {
    if (!targetModal) return;
    setModalFlags((prev) => ({ ...prev, [targetModal]: false }));
  };

  const updateColumnPreferences = async (reorderedKpiColumns) => {
    // Dedupe defensively — a duplicate id in the saved payload would trip the
    // server's "Duplicate KPI ID/s" validation and permanently 500 on every
    // future save for this workspace until someone edits the DB by hand.
    const orderedIds = [
      ...new Set(
        reorderedKpiColumns
          .filter((kpi, index) => ![0, 1].includes(index))
          .map((kpi) => kpi._id),
      ),
    ];
    const payload = {
      groupId: workspace._id,
      category: PREFERENCES_CATEGORY.KPI_COLUMN_ORDERING,
      kpiColumns: orderedIds,
    };

    // Mirror the drag into the local prefs so a later groupKpis update
    // rebuilds with the NEW order instead of snapping back to the fetched one.
    setColumnPrefs((prev) => (prev ? { ...prev, kpiColumns: orderedIds } : prev));

    // The shared dev/prod Atlas cluster throws transient network timeouts
    // fairly often — retry a couple times with backoff before bothering the
    // user, since this save is idempotent (full replace, safe to repeat).
    const RETRY_DELAYS_MS = [500, 1500];
    const delay = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });
    const attemptSave = async (remainingDelays) => {
      try {
        await SET_KPI_PREFERENCES(payload);
      } catch (error) {
        if (!remainingDelays.length) {
          toast.error(
            "Your column preferences were not saved. Please refresh the browser and try again.",
          );
          return;
        }
        await delay(remainingDelays[0]);
        await attemptSave(remainingDelays.slice(1));
      }
    };
    await attemptSave(RETRY_DELAYS_MS);
  };

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;
    // result.destination.index is an index into the RENDERED (visible)
    // columns, not the full `columns` state — see visibleColumns above.
    if (disabledColumns.includes(visibleColumns[result.destination.index]?.name))
      return;

    const revisedVisibleColumns = [...visibleColumns];
    const eId = revisedVisibleColumns.findIndex(
      (el) => el._id === result.draggableId,
    );
    if (eId === -1) return;

    // Remove col name at the selected position
    revisedVisibleColumns.splice(eId, 1);

    // Re-attach removed col name to the new position
    revisedVisibleColumns.splice(result.destination.index, 0, visibleColumns[eId]);

    // Re-thread any columns hidden by the current filter back in (at the end
    // — they aren't on screen right now, so their exact position doesn't
    // matter) so a save never drops a metric's saved ordering entirely.
    const visibleIds = new Set(visibleColumns.map((c) => c._id));
    const hiddenColumns = columns.filter((c) => !visibleIds.has(c._id));
    const revisedOrderColumns = [...revisedVisibleColumns, ...hiddenColumns];

    updateColumnPreferences(revisedOrderColumns);
    setColumns(revisedOrderColumns);
  };

  // Adjust the draggable column styles when dragging. The visible "lifted
  // column" card is drawn by the ::before below (full column height); here we
  // just keep the header fully opaque, on top, and grabbing.
  const getStyle = (style, snapshot) => {
    // While dragging, the header element itself becomes the lifted column card
    // (its values are rendered inside it during drag, below). Style it as an
    // elevated card so the whole column reads as picked up.
    if (snapshot.isDragging) {
      return {
        ...style,
        opacity: 1,
        background: "#ffffff",
        border: "1px solid #E4E7EC",
        borderRadius: "12px",
        cursor: "grabbing",
        zIndex: 40,
        boxShadow:
          "0 24px 50px -12px rgba(16, 24, 40, 0.32), 0 8px 18px -8px rgba(16, 24, 40, 0.18)",
      };
    }
    return { ...style };
  };

  const isIncrementalModeDisabled = useCallback(
    (kpiId) => {
      if (Object.keys(metricSettingsData).length === 0) return;
      // Metrics whose entries must carry details (Memberships records a
      // customer and plan name on every entry) can never be edited as a bare
      // total — a typed total writes an entry with NO metadata, so the plan
      // splits (bonus "only if" conditions) silently miss it. Force the
      // entry modal for them regardless of the Total Score setting.
      //
      // The generic note field every "Text" submission metric gets by default
      // (text_entry / description / note) is NOT such a detail — nothing splits
      // on it, and treating it as one locked every text metric (e.g. Rev Cred
      // Hr) out of Total Score mode, so typed "totals" became added entries.
      const GENERIC_NOTE_KEYS = ["text_entry", "description", "note", "notes"];
      const kpi = groupKpis?.find((k) => k._id === kpiId);
      const needsDetails = kpi?.requiredInputs?.some(
        (input) =>
          (input.type === "text" || input.type === "dropdown") &&
          !input.optional &&
          !GENERIC_NOTE_KEYS.includes(String(input.value ?? "").trim().toLowerCase()),
      );
      if (needsDetails) return false;
      const setting = metricSettingsData.find((x) => x.kpiId === kpiId);
      if (setting) return setting.isTotalScore;

      // Hours Worked is a running MONTH TOTAL (HCP-synced hours + manual
      // adjustments layered on top via useTotalScoreSave/hoursWorkedManual.js)
      // — never a per-entry counter. Without an explicit Total Score setting
      // this fell through to the generic "+1 Item" add modal (kind === "#"),
      // which can't accept a real hour value at all. Default true regardless
      // of whether the setting has ever been configured for this metric.
      const isHoursWorked =
        kpi?.model?.type === "hours_worked" || kpi?.type === "hours_worked";
      if (isHoursWorked) return true;

      return false;
    },
    [metricSettingsData, groupKpis],
  );

  const { saveTotalScore } = useTotalScoreSave();

  // Which Total Score cells are mid-save, keyed by `${playerId}:${kpiId}`.
  const [savingCells, setSavingCells] = useState(() => ({}));

  const cellSaveKey = (playerId, kpiId) => `${playerId}:${kpiId}`;

  const handleTotalScoreSave = useCallback(async (kpiId, playerId, newValue) => {
    if (!Number.isFinite(newValue)) {
      throw new Error("Enter a valid number");
    }
    const key = cellSaveKey(playerId, kpiId);
    const startedAt = Date.now();
    setSavingCells((prev) => ({ ...prev, [key]: true }));

    // Mark composite cells that depend on this source KPI as pending.
    // They will show a spinner until the background refresh completes.
    const affectedComposites = (groupKpis || []).filter(
      (kpi) =>
        isCompositeKpi(kpi) &&
        (String(kpi.optional?.sourceId1) === String(kpiId) ||
          String(kpi.optional?.sourceId2) === String(kpiId)),
    );
    if (affectedComposites.length) {
      setPendingCompositeCells((prev) => {
        const next = new Set(prev);
        affectedComposites.forEach((kpi) =>
          next.add(cellSaveKey(playerId, String(kpi._id))),
        );
        return next;
      });
    }

    try {
      await saveTotalScore(kpiId, playerId, newValue, monthRef);
      // Clear as soon as save (+ server composite recompute) finishes. Waiting only
      // on kpiReducer.isLoading left cells stuck when GetKpis hung after save.
      if (affectedComposites.length) {
        setPendingCompositeCells((prev) => {
          if (prev.size === 0) return prev;
          const next = new Set(prev);
          affectedComposites.forEach((kpi) =>
            next.delete(cellSaveKey(playerId, String(kpi._id))),
          );
          return next;
        });
      }
    } catch (err) {
      if (affectedComposites.length) {
        setPendingCompositeCells((prev) => {
          if (prev.size === 0) return prev;
          const next = new Set(prev);
          affectedComposites.forEach((kpi) =>
            next.delete(cellSaveKey(playerId, String(kpi._id))),
          );
          return next;
        });
      }
      throw err;
    } finally {
      const waitMs = Math.max(0, 800 - (Date.now() - startedAt));
      if (waitMs > 0) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, waitMs);
        });
      }
      setSavingCells((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }, [saveTotalScore, monthRef, groupKpis]);

  const handleAdvanceRow = useCallback((rowIndex, colIndex, inputEl, filterUsersDataLength) => {
    if (filterUsersDataLength === 1) {
      inputEl?.blur();
      return;
    }
    const nextId =
      rowIndex === filterUsersDataLength - 1
        ? `cell-0-${colIndex}`
        : `cell-${rowIndex + 1}-${colIndex}`;
    document.getElementById(nextId)?.focus();
  }, []);

  const getOriginalCellValue = useCallback((user, kpiId, kpiKind, kpiMeta = null) => {
    let value;

    // Month buckets can key scores by ObjectId or string for the same KPI —
    // resolve to whichever key actually holds the score before reading it.
    const monthKey = resolveMonthKpiKey(user.month, kpiId);
    if (user.month && user.month[monthKey]) {
      value = user.month[monthKey].amt ?? 0;
    } else if (user.kpis && typeof user.kpis === "object" && user.kpis[kpiId] != null) {
      value = user.kpis[kpiId] ?? 0;
    } else {
      value = 0;
    }

    // Scorekeeper display: round whole-number amounts (legacy behavior before
    // decimal pass) — abs() so negatives round too instead of showing decimals.
    if (Math.abs(value) >= 1) {
      value = Math.round(value);
    }

    // New: Display formatted numbers.
    // Formatted numbers can be a Number, or a String (Number/Decimal with "$", "%" and "," symbols)
    const formatted = kpiKind === "%" ? formatKpiPercent(kpiMeta, value) : formatNumber(value, kpiKind);

    return {
      orig: value,
      // For consistency, set Number values to String to enable Regex /$%,/g removal
      formatted: formatted?.toString(),
    };
  }, []);

  // Memoized filter and row data calculation
  const filterUsersData = useMemo(() => {
    if (!Array.isArray(usersKpis) || usersKpis.length === 0) return [];
    if (filterIndex === 0) {
      // Sort by points, do not mutate usersKpis
      return [...usersKpis].sort((a, b) => b.total - a.total);
    } else {
      // Sort by name, do not mutate usersKpis
      return [...usersKpis].sort((a, b) => {
        const firstA = a.info.firstName.toLowerCase();
        const firstB = b.info.firstName.toLowerCase();
        if (firstA < firstB) {
          return -1;
        }
        if (firstA > firstB) {
          return 1;
        }
        return 0;
      });
    }
  }, [usersKpis, filterIndex]);

  // Compute rank movement vs the previous week — drives the small ↑/↓ pill
  // next to each player name. Localstorage-backed; no backend required.
  const { deltasByUserId: rankDeltas } = useRankHistory(
    filterUsersData,
    company?._id,
  );

  // Memoized rowsData calculation
  const rowsData = useMemo(() => {
    if (
      !filterUsersData ||
      filterUsersData.length === 0 ||
      !groupKpis ||
      Object.keys(metricSettingsData).length === 0
    )
      return [];

    return filterUsersData.map((user, rowIndex) => {
      const rowContainer = {};

      // Index [5] carries the optional <RankPill /> element rendered
      // next to the player name. SoftTableRow above expects this slot.
      const delta = rankDeltas[user.userId];
      rowContainer.User = [
        user.info.image,
        `${user.info.firstName} ${user.info.lastName}`,
        user.info.email,
        user.userId,
        user.info.isActive,
        delta != null ? <RankPill delta={delta} /> : null,
      ];

      rowContainer.Points = (
        <SoftTypography
          sx={{
            fontFamily: "DM Sans",
            color: "#191D23",
            fontSize: "18px",
            fontWeight: 600,
            fontFeatureSettings: '"tnum" 1',
            letterSpacing: "-0.01em",
          }}
        >
          {formatNumber(Math.round(Math.max(0, user.total ?? 0)), "#")}
        </SoftTypography>
      );

      groupKpis?.forEach((kpi, index) => {
        const cellValue = getOriginalCellValue(user, kpi._id, kpi?.kind, kpi);

        const cellKey = `${user.userId}-${kpi._id}`;
        const cellCelebration = celebrations[cellKey];
        const cellNode = renderScorekeeperCell({
          kpi,
          groupKpis,
          compositeNameLookup,
          cellValue,
          user,
          rowIndex,
          index,
          isTotalScoreMode: isIncrementalModeDisabled(kpi._id),
          onSave: handleTotalScoreSave,
          onAdvanceRow: handleAdvanceRow,
          filterUsersDataLength: filterUsersData.length,
          isSavingExternal:
            !!savingCells[cellSaveKey(user.userId, kpi._id)] ||
            pendingCompositeCells.has(cellSaveKey(user.userId, String(kpi._id))),
          monthKey: monthRef?.format?.("YYYY-MM") ?? null,
        });

        rowContainer[kpi.title] = groupKpis && (
          <SoftBox key={kpi._id}>
            {isCompositeKpi(kpi) ? (
              cellNode
            ) : (
              <KpiModal
                kpi={kpi}
                userId={user.userId}
                index={index}
                month={monthRef}
                disabled={isIncrementalModeDisabled(kpi._id)}
                userPoints={cellValue?.orig}
                onSave={(payload) =>
                  triggerCelebration(
                    user.userId,
                    kpi._id,
                    payload?.amount,
                    payload?.kind ?? kpi?.kind,
                    kpi,
                  )
                }
                sx={{
                  width: "100%",
                }}
              >
                {cellNode}
                <CellCelebration
                  triggerKey={cellCelebration?.key}
                  amount={cellCelebration?.amount}
                />
              </KpiModal>
            )}
          </SoftBox>
        );
      });
      return rowContainer;
    });
  }, [
    filterUsersData,
    groupKpis,
    compositeNameLookup,
    monthRef,
    metricSettingsData,
    isIncrementalModeDisabled,
    handleTotalScoreSave,
    getOriginalCellValue,
    savingCells,
    pendingCompositeCells,
    rankDeltas,
    celebrations,
    triggerCelebration,
  ]);

  useEffect(() => {
    setKpiId(isSelected);
    setIsLoading(true);
  }, [isSelected]);

  useEffect(() => {
    const handleClick = () => {
      if (isLoading) {
        setKpiId(null);
        setIsLoading(false);
      }
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [isLoading]);

  // Memoized renderRows - added kpiIdparam dependency to ensure re-render when it changes
  const ROW_HEIGHT = 76;

  // Renders one cell for a given row + column. Reuses the EXACT row[name]
  // content (values, "+" add buttons, click handlers) so cell behaviour is
  // unchanged — only the layout (column-oriented) differs. Fixed height keeps
  // rows aligned horizontally across the column stacks.
  const renderCell = (row, name, align, isLast, rowIndex, isFirstCol, isLastCol) => {
    const inactive = `${row.User?.[4]}` === "false";
    // Mobile-only "card row" treatment: a light zebra stripe on active rows
    // (the desktop grid is dense enough to read without one) and rounded
    // outer corners on the first/last column so each row reads as a distinct
    // rounded band instead of one flat, edge-to-edge block on a phone.
    const zebra = isMobile && !inactive && rowIndex % 2 === 1;
    let borderBottomStyle = "none";
    if (!isMobile && !isLast) {
      borderBottomStyle = `${borderWidth[1]} solid ${light.main}`;
    }
    let cellBg = "transparent";
    if (inactive) {
      cellBg = "#EAECF0";
    } else if (zebra) {
      cellBg = "#F9FAFB";
    }
    const cellSx = {
      // Fixed (not min) height so every column's rows line up exactly — a
      // column stack can't auto-sync row heights the way the old grid did.
      height: `${ROW_HEIGHT}px`,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      padding: "8px",
      boxSizing: "border-box",
      borderBottom: borderBottomStyle,
      marginBottom: isMobile && !isLast ? "6px" : 0,
      background: cellBg,
      ...(isMobile && isFirstCol && { borderTopLeftRadius: "10px", borderBottomLeftRadius: "10px" }),
      ...(isMobile && isLastCol && { borderTopRightRadius: "10px", borderBottomRightRadius: "10px" }),
    };
    if (Array.isArray(row[name])) {
      return (
        <SoftBox
          sx={{ ...cellSx, cursor: "pointer", gap: "8px" }}
          onClick={() => navigate(`/scorekeeper/timeline?user=${row[name][3]}`)}
        >
          <SoftProfile src={row[name][0]} name={row[name][1]} size="md" borderRadius="md" />
          <SoftBox sx={{ minWidth: 0 }}>
            <SoftBox sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <SoftTypography
                sx={{
                  fontFamily: "DM Sans",
                  color: "#191D23",
                  fontWeight: 500,
                  fontSize: "15px",
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row[name][1]}
              </SoftTypography>
              {row[name][5] && <SoftBox sx={{ flexShrink: 0 }}>{row[name][5]}</SoftBox>}
            </SoftBox>
            <SoftTypography
              sx={{ fontFamily: "DM Sans", color: "#98A2B3", fontSize: "12px", fontWeight: 400, marginTop: "2px" }}
            >
              {row[name][2]}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      );
    }
    const value = row[name];
    // The KPI value is a React element (the score box + "+"); it should FILL
    // the cell like it did in the old grid. Plain values (e.g. Points) are
    // text and just center.
    const isElement = React.isValidElement(value);
    return (
      <SoftBox
        sx={{
          ...cellSx,
          color: "#343A40",
          justifyContent: align === "left" ? "flex-start" : "center",
          ...(isElement && { "& > *": { width: "100%" } }),
        }}
      >
        {value}
      </SoftBox>
    );
  };

  const renderColumns = useMemo(() => {
    // Only render headers for KPIs still in the scoreboard list (User/Points
    // stay) — see visibleColumns above, shared with handleOnDragEnd so drag
    // indices line up with what's actually on screen.
    return visibleColumns.map(({ _id, kpiLinks, name, align, width, icon, scorekeeperOnly }, key) => {
      let pl;
      let pr;

      if (key === 0) {
        pl = 3;
        pr = 3;
      } else if (key === visibleColumns.length - 1) {
        pl = 3;
        pr = 3;
      } else {
        pl = 1;
        pr = 1;
      }

      const mWidth =
        name === "User" ? TABLE_USER_COLUMN_WIDTH : TABLE_COLUMN_WIDTH;

      return (
        <Draggable
          key={_id}
          index={key}
          draggableId={_id}
          isDragDisabled={disabledColumns.includes(name)}
        >
          {(provided, snapshot) => (
            <SoftBox
              key={name}
              ref={provided.innerRef}
              {...provided.draggableProps}
              style={getStyle(provided.draggableProps.style, snapshot)}
              sx={{
                // Fill the row proportionally on wide screens (User 1.5fr, each
                // KPI exactly 1fr — flex-basis 0 so content width can't skew the
                // split) but never shrink below the designed column width — past
                // that the table overflows into the horizontal scroll wrapper
                // instead of crushing the cells.
                flex: name === "User" ? "1.5 1 0px" : "1 1 0px",
                minWidth: mWidth,
                display: "flex",
                flexDirection: "column",
                fontSize: size.md,
                background: (() => {
                  if (kpiIdparam === _id && !snapshot.isDragging) return "rgba(152, 162, 179, 0.08)";
                  // Faint warm band down scorekeeper-only columns so the
                  // marker reads as a column, not just a header pill.
                  if (scorekeeperOnly) return "rgba(254, 243, 230, 0.55)";
                  return "transparent";
                })(),
                border:
                  kpiIdparam === _id && !snapshot.isDragging
                    ? "1px solid rgba(255, 29, 143, 0.25)"
                    : "1px solid transparent",
                borderRadius: "8px",
              }}
            >
              <SoftBox
                {...provided.dragHandleProps}
                sx={{
                  height: "56px",
                  flexShrink: 0,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "0 8px",
                  cursor: snapshot.isDragging ? "grabbing" : "grab",
                  borderBottom: `${borderWidth[1]} solid ${light.main}`,
                  background: "#FFFFFF",
                  // Pinned below the app's fixed 56px top bar so it stays
                  // visible while scrolling down the player list, instead of
                  // scrolling away with the rows. Dropped while a column is
                  // actively being dragged — sticky's containing-block math
                  // doesn't compose cleanly with react-beautiful-dnd's own
                  // translate transform on the column.
                  ...(!snapshot.isDragging && {
                    position: "sticky",
                    top: "56px",
                    zIndex: 2,
                  }),
                  // A faint tinted band on mobile separates the header from
                  // the card-style rows below it more clearly than the thin
                  // border alone did on a phone.
                  ...(isMobile && {
                    background: "#F9FAFB",
                    borderRadius: `${key === 0 ? "10px" : "0"} ${key === visibleColumns.length - 1 ? "10px" : "0"} 0 0`,
                  }),
                }}
              >
                <SoftBox
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "90%",
                    paddingLeft: `${icon ? "0" : "8px"}`,
                    "& h6": {
                      textAlign: "center",
                      lineHeight: "normal",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <SoftBox>{icon}</SoftBox>
                  {/* Scorekeeper-only metrics are marked by COLOUR ALONE — the
                      amber title plus the faint warm tint down the column. An
                      earlier pill + crossed-eye badge boxed the title, which
                      forced a two-line wrap in narrow columns and made this
                      header taller than every other one. The header now stays a
                      plain single line like the rest of the row; the hover
                      title still spells the meaning out. */}
                  <SoftBox
                    title={scorekeeperOnly ? "Scorekeeper only — players never see this metric" : undefined}
                    sx={{ display: "inline-flex", alignItems: "center", minWidth: 0 }}
                  >
                    <SoftTypography
                      sx={{
                        fontFamily: "DM Sans",
                        color: scorekeeperOnly ? "#B54708" : "#475467",
                        fontSize: "12px",
                        fontWeight: scorekeeperOnly ? 600 : 500,
                        letterSpacing: "0.01em",
                      }}
                    >
                      {name}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>

                {key > 1 ? (
                  <SoftBox zIndex={2}>
                    <PopupState variant="popover" popupId={_id}>
                      {(popupState) => (
                        <>
                          <SoftButton
                            {...bindTrigger(popupState)}
                            variant="text"
                            sx={{
                              minWidth: "max-content",
                              minHeight: "max-content",
                              boxShadow: "none",
                              padding: 0,
                              "& svg": {
                                fontSize: "1.5rem !important",
                                color: "rgba(108, 117, 125, 1)",
                              },
                            }}
                          >
                            <MoreVertIcon />
                          </SoftButton>
                          <Popover
                            {...bindPopover(popupState)}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "center",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                            sx={{
                              "& .MuiPaper-root": {
                                backgroundColor: "#FBFDFF",
                                boxShadow:
                                  "12px 12px 24px 0px rgba(0, 0, 0, 0.12)",
                                border: "1px solid #E9ECEF",
                                borderRadius: "4px",
                              },
                            }}
                          >
                            <SoftBox
                              display="flex"
                              flexDirection="column"
                              gap="0.25rem"
                              alignItems="center"
                            >
                              <SoftButton
                                variant="text"
                                sx={{
                                  justifyContent: "start",
                                  color: "#343A40",
                                  textTransform: "none",
                                  width: "100%",
                                  "&:hover": {
                                    color: "#343A40",
                                  },
                                  "&:focus:not(:hover)": {
                                    color: "#343A40",
                                  },
                                }}
                                onClick={() => {
                                  popupState.close();
                                  navigate(`/scorekeeper/timeline?kpi=${_id}`);
                                }}
                              >
                                <TimelineIcon
                                  style={{ marginRight: "0.25rem" }}
                                />{" "}
                                Metric Timeline
                              </SoftButton>
                              <SoftButton
                                variant="text"
                                sx={{
                                  justifyContent: "start",
                                  color: "#343A40",
                                  textTransform: "none",
                                  width: "100%",
                                  "&:hover": {
                                    color: "#343A40",
                                  },
                                  "&:focus:not(:hover)": {
                                    color: "#343A40",
                                  },
                                }}
                                onClick={() => {
                                  popupState.close();
                                  handleOpenModal({
                                    kpiId: _id,
                                    kpiLinks: kpiLinks?.links,
                                    targetModal: MODAL_MANAGE_LINKS,
                                  });
                                }}
                              >
                                <AttachLinkIcon
                                  style={{ marginRight: "0.25rem" }}
                                />
                                Manage Links
                              </SoftButton>
                              <SoftButton
                                variant="text"
                                sx={{
                                  justifyContent: "start",
                                  color: "#343A40",
                                  textTransform: "none",
                                  width: "100%",
                                  "&:hover": {
                                    color: "#343A40",
                                  },
                                  "&:focus:not(:hover)": {
                                    color: "#343A40",
                                  },
                                }}
                                onClick={() => {
                                  popupState.close();
                                  handleOpenModal({
                                    kpiId: _id,
                                    targetModal: MODAL_METRIC_SCORE,
                                  });
                                }}
                              >
                                <MetricScoreIcon
                                  style={{ marginRight: "0.25rem" }}
                                />
                                Metric Score Configuration
                              </SoftButton>
                            </SoftBox>
                          </Popover>
                        </>
                      )}
                    </PopupState>
                  </SoftBox>
                ) : null}
              </SoftBox>

              {/* The column's cells. Because the whole column (header + cells)
                  is one Draggable, dragging lifts it as a unit, leaves a live
                  gap where it was, and the other columns slide over as you drag. */}
              {rowsData.map((row, ri) => (
                <React.Fragment key={row.User?.[3] || ri}>
                  {renderCell(row, name, align, ri === rowsData.length - 1, ri, key === 0, key === visibleColumns.length - 1)}
                </React.Fragment>
              ))}
            </SoftBox>
          )}
        </Draggable>
      );
    });
  }, [visibleColumns, navigate, kpiIdparam, rowsData, disabledColumns, size, borderWidth, light, TABLE_USER_COLUMN_WIDTH, TABLE_COLUMN_WIDTH, getStyle]);


  return (
    <>
      {modalFlags[MODAL_ADD_LINK] && (
        <AddLinkModal
          isOpen={modalFlags[MODAL_ADD_LINK]}
          kpiId={currentKpiId}
          kpiLinksData={currentKpiLinksData}
          onModalClose={() => handleCloseModal(MODAL_ADD_LINK)}
          groupKpis={groupKpis}
        />
      )}

      {modalFlags[MODAL_VIEW_LINKS] && (
        <ViewAllLinksModal
          isOpen={modalFlags[MODAL_VIEW_LINKS]}
          kpiLinksData={currentKpiLinksData}
          onModalClose={() => handleCloseModal(MODAL_VIEW_LINKS)}
          groupKpis={groupKpis}
        />
      )}

      {modalFlags[MODAL_METRIC_SCORE] && (
        <MetricScoreConfig
          workspaceId={workspace?._id}
          kpiId={currentKpiId}
          data={metricSettingsData.find((x) => x.kpiId === currentKpiId)}
          openConfig={modalFlags[MODAL_METRIC_SCORE]}
          handleClose={() => handleCloseModal(MODAL_METRIC_SCORE)}
          /* Preferences are fetched once per workspace (see columnPrefs), so a
             just-saved entry mode must be mirrored into that state — otherwise
             the next groupKpis-driven rebuild would resurrect the old mode. */
          handleOnSuccess={({ kpiId: savedKpiId, scoreEntryMode }) => {
            setColumnPrefs((prev) => {
              if (!prev) return prev;
              const modes = (prev.scoreEntryModes || []).filter((x) => x._id !== savedKpiId);
              return {
                ...prev,
                scoreEntryModes: [...modes, { _id: savedKpiId, mode: scoreEntryMode }],
              };
            });
          }}
        />
      )}

      {modalFlags[MODAL_MANAGE_LINKS] && (
        <Dialog
          open={modalFlags[MODAL_MANAGE_LINKS]}
          onClose={() => handleCloseModal(MODAL_MANAGE_LINKS)}
          sx={{
            ".MuiPaper-root": {
              padding: { mini: "1rem", md: "1rem 1.5rem" },
              borderRadius: "8px",
              // glass
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%)",
              boxShadow:
                "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
              backdropFilter: "blur(18px) saturate(160%)",
              width: "100%",
              maxWidth: "600px",
              border: "1px solid rgba(255,255,255,0.6)",
            },
          }}
        >
          <ManageLinks
            kpiLinksData={currentKpiLinksData}
            onManageLinksClick={(params) => {
              handleOpenModal({
                kpiId: params.kpiId,
                targetModal: MODAL_VIEW_LINKS,
                kpiLinks: params.kpiLinks,
              });
            }}
            onAddLinkButtonClick={(params) => {
              handleOpenModal({
                kpiId: params.kpiId,
                targetModal: MODAL_ADD_LINK,
                kpiLinks: params.kpiLinks,
              });
            }}
            _id={currentKpiId}
          />
        </Dialog>
      )}
      {useMemo(
        () => (
          <SoftBox
            sx={{
              boxShadow: "none",
              borderRadius: 0,
            }}
          >
            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable
                droppableId="droppable-col-names"
                direction="horizontal"
              >
                {(provided) => (
                  <SoftBox
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                    }}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {renderColumns}
                    {provided.placeholder}
                  </SoftBox>
                )}
              </Droppable>
            </DragDropContext>
          </SoftBox>
        ),
        [columns, renderColumns]
      )}
    </>
  );
}

export default Table;
